import {
    faFolder as solidFaFolder,
    faFolderOpen as solidFaFolderOpen,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ActionModal, Label } from '@kcftech/design-system';
import {
    ContentType,
    IFolderResponse,
    MoveFilesRequest,
} from '@kcftech/prometheus-client';
import { useMutation } from '@tanstack/react-query';
import { SpinnerArea } from 'components/spinner/SpinnerArea';
import { useNotificationContext } from 'contexts/NotificationContext';
import { sdClient } from 'contexts/SDKClientContext';
import { useInfiniteDirectoryDataSource } from 'custom_hooks/data_hooks/data_source/directoryDataSource';
import { useDrainDataSource } from 'custom_hooks/data_hooks/data_source/useDrainDataSource';
import { useEffect, useMemo, useState } from 'react';
import { DataTree } from 'shared/data_tree/DataTree';
import { createTree } from 'shared/data_tree/tree';
import {
    TreeExpansionConfig,
    TreeNodeData,
    TreeNodeRenderer,
    TreeSelectionConfig,
} from 'shared/data_tree/types';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';
import { toggleArrayItem, toMap } from 'utils/array';
import { queryClient } from 'utils/query';

import { FileIsh } from '../FileLibraryActionsCell';
import { PathToIdMapping } from '../hooks/useFolderIdsInPath';

export type MoveFolderModalProps = Readonly<{
    /**
     * Selected file/folder to be moved
     */
    fileToBeMoved: FileIsh;
    /**
     * Callback for when move was successful
     */
    onSave: () => void;
    /**
     * Callback for when move was cancelled
     */
    onCancel: () => void;
    locationId?: string;
    corporationId?: string;
    foldersHierarchy: PathToIdMapping[];
}>;

const FolderItem = styled.span<{ $selected: boolean }>`
    background: ${({ $selected }) => ($selected ? tokens.text.link : 'none')};
    color: ${({ $selected }) => ($selected ? tokens.on.primary : 'none')};
    padding: 0.25rem;
`;

const FileToBeMovedItem = styled.span`
    color: ${tokens.text.link};
`;

type Folder = IFolderResponse;

const renderNode: TreeNodeRenderer<Folder> = (info) =>
    info.isSelectable ? (
        <FolderItem $selected={info.isSelected}>
            <FontAwesomeIcon
                icon={info.isExpanded ? solidFaFolderOpen : solidFaFolder}
                color={tokens.warning.default}
                className="mr-5"
            />
            {info.node.data.Name}
        </FolderItem>
    ) : (
        <FileToBeMovedItem>
            <FontAwesomeIcon icon={solidFaFolderOpen} className="mr-5" />
            {info.node.data.Name}
        </FileToBeMovedItem>
    );

export function DirentMoveModal({
    fileToBeMoved,
    onSave,
    onCancel,
    locationId,
    corporationId,
    foldersHierarchy,
}: MoveFolderModalProps) {
    const { showToast } = useNotificationContext();
    const [destinationFolder, setDestinationFolder] = useState<Folder>();
    const [tree, setTree] = useState(() => createTree<Folder>());
    const [expandedFolderIds, setExpandedFolderIds] = useState<string[]>([]);
    const parentIds = useMemo(
        () => foldersHierarchy.map((folder) => folder.id),
        [foldersHierarchy],
    );

    const { results: _folders, isLoading: isLoadingFolders } = useDrainDataSource(
        useInfiniteDirectoryDataSource({
            name: 'file-library-table',
            defaultFilter: {
                corporationId,
                locationId,
                fileTypes: [ContentType.Folder],
                folderPath: '/',
                recursive: true,
            },
        }),
    );
    const folders = _folders as IFolderResponse[] | undefined;

    const { mutate: moveFolder, isPending: isMovingFolder } = useMutation({
        mutationFn: async () => {
            if (!destinationFolder) {
                throw new Error('Missing destination folder');
            }
            await sdClient.moveFiles({
                body: new MoveFilesRequest({
                    FileIds: [fileToBeMoved.Id],
                    DestinationFolderId: destinationFolder.Id,
                }),
            });
        },

        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['file-library-table'],
            });
            onSave();
            showToast({
                type: 'success',
                content: `${fileToBeMoved.Name} successfully moved to ${destinationFolder?.Name}.`,
            });
        },

        onError: () => {
            showToast({
                type: 'error',
                content: `An error occurred while moving ${fileToBeMoved.Name}.`,
            });
        },
    });

    const expandable = useMemo<TreeExpansionConfig<Folder>>(
        () => ({
            isExpanded: (node) => expandedFolderIds.includes(node.id),
            onChange: (event) => {
                setExpandedFolderIds((current) =>
                    toggleArrayItem(current, event.node.id),
                );
            },
            canExpand: ({ data, children }) =>
                !!children && children.length > 0 && data.Id !== fileToBeMoved.Id,
            isLoading: () => isLoadingFolders,
        }),
        [expandedFolderIds, fileToBeMoved.Id, isLoadingFolders],
    );

    const selectable = useMemo<TreeSelectionConfig<Folder>>(
        () => ({
            selectionStyle: 'custom',
            isSelected: (node) => destinationFolder === node.data,
            onChange: ({ node }) => setDestinationFolder(node.data),
            canSelect: (node) => node.id !== fileToBeMoved.Id,
        }),
        [destinationFolder, fileToBeMoved.Id],
    );

    useEffect(() => {
        if (folders) {
            const foldersByPath = toMap<Folder, string, TreeNodeData<Folder>>(
                folders,
                (f) => f.Path,
                (f) => ({ id: f.Id, data: f, children: [] }),
            );

            const parentPathPattern = /(?<=\/)[^/]*\/$/;
            function toParentPath(path: string) {
                return path.replace(parentPathPattern, '');
            }
            for (const node of foldersByPath.values()) {
                if (node.data.Path === '/') {
                    continue;
                }
                const parent = foldersByPath.get(toParentPath(node.data.Path));
                if (parent) {
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    parent.children!.push(node);
                }
            }

            const root = foldersByPath.get('/');
            setTree(createTree<Folder>(root ? [root] : undefined));
        }
    }, [folders]);

    useEffect(() => {
        if (!fileToBeMoved.ParentFolderId) {
            throw new Error('Missing parent folder');
        }
        setExpandedFolderIds([fileToBeMoved.ParentFolderId, ...parentIds]);
    }, [fileToBeMoved.ParentFolderId, foldersHierarchy, parentIds]);

    const title = useMemo(
        () => (
            <div style={{ color: tokens.text.secondary }}>
                {'Move '}
                {fileToBeMoved.EntryType === ContentType.Folder ? 'folder ' : 'file '}
                <span style={{ color: tokens.text.primary }}>{fileToBeMoved.Name}</span>
                {' to '}
                <span style={{ color: tokens.text.primary }}>
                    {destinationFolder?.Name ?? '...'}
                </span>
            </div>
        ),
        [destinationFolder?.Name, fileToBeMoved.EntryType, fileToBeMoved.Name],
    );

    return (
        <ActionModal
            data-testid="dirent-move-modal"
            height={500}
            width={750}
            title={title}
            actionButtonText="Move"
            actionable={!isMovingFolder && destinationFolder !== undefined}
            closeButtonText="Cancel"
            onClose={onCancel}
            onAction={moveFolder}
        >
            <Label label="Select a Folder">
                <SpinnerArea isLoading={isLoadingFolders}>
                    <DataTree
                        treeId="folder-tree"
                        data={tree}
                        renderNode={renderNode}
                        expandable={expandable}
                        selectable={selectable}
                    />
                </SpinnerArea>
            </Label>
        </ActionModal>
    );
}
